import {castDraft, type Draft} from 'immer';

import type {AssistantContentPart, RuntimeEvent} from '../runtime/events';
import {assistantMessageId} from '../runtime/turn';
import {addMessageNode, findToolCallPart} from './messageTree';
import {
  type ContentPart,
  type MessageNode,
  type ReadonlyJSONValue,
  type RunOutcome,
  type Thread,
  type ToolCallPart,
} from './thread';

/** applyRuntimeEventToThread applies a runtime event to a thread draft. */
export function applyRuntimeEventToThread(
  thread: Draft<Thread>,
  event: RuntimeEvent,
  now: number
): void {
  switch (event.type) {
    case 'turn-created': {
      // Adopt the server's turn-creation time so lastActivityAt is on the
      // server clock right away (consistent with mergeThreadSummaries). Math.max
      // keeps it monotonic vs. the optimistic client-send bump in addUserMessage.
      thread.lastActivityAt = Math.max(
        thread.lastActivityAt,
        event.lastActivityAt
      );
      thread.latestTurnId = event.turnId;
      break;
    }
    case 'user-message': {
      const node: MessageNode = {
        id: event.messageId,
        role: 'user',
        content: event.content,
        createdAt: now,
      };
      addMessageNode(
        thread.messageTree,
        thread.messageTree.currentNodeId,
        node
      );
      break;
    }
    case 'run-started': {
      thread.status = 'running';
      thread.currentRunId = event.runId;
      thread.error = null;
      // A new run starts fresh; drop any lingering cancel intent. A resumed
      // run re-attaches to the same turn, so its pending cancel stays.
      if (!event.resumed) {
        thread.cancelRequested = false;
      }
      // A new run answers (or abandons) any prior questions; clear them so the
      // composer reverts to its normal state for this turn.
      thread.pendingQuestions = null;
      // Likewise a new run resolves (grants/denies) any pending network access
      // request. Active grants are tracked separately in activeNetworkDomains,
      // refreshed from each completed turn's resolved permissions.
      thread.pendingNetworkAccessRequest = null;
      break;
    }
    case 'agent-questions': {
      thread.pendingQuestions = event.questions;
      break;
    }
    case 'network-access-request': {
      thread.pendingNetworkAccessRequest = event.request;
      break;
    }
    case 'network-permissions': {
      thread.activeNetworkDomains = event.domains;
      break;
    }
    case 'run-finished': {
      settleRun(thread, event.runId, 'finished', now);
      break;
    }
    case 'run-cancelled': {
      settleRun(thread, event.runId, 'cancelled', now);
      break;
    }
    case 'run-error': {
      settleRun(thread, event.runId, 'errored', now, event.message);
      break;
    }

    case 'text-message-start':
      handleStreamingStart(thread, 'text', event.messageId, now);
      break;
    case 'text-message-content':
      handleStreamingDelta(thread, 'text', event.messageId, event.delta);
      break;
    case 'text-message-end':
      handleStreamingEnd(thread, 'text', event.messageId);
      break;

    case 'reasoning-start':
      handleStreamingStart(thread, 'thinking', event.messageId, now);
      break;
    case 'reasoning-content':
      handleStreamingDelta(thread, 'thinking', event.messageId, event.delta);
      break;
    case 'reasoning-end':
      handleStreamingEnd(thread, 'thinking', event.messageId);
      break;
    case 'assistant-content-replace':
      replaceAssistantContent(thread, event.messageId, event.content, now);
      break;

    case 'tool-call-start': {
      if (findToolCallPartInThread(thread, event.toolCallId)) {
        break;
      }
      const msg = ensureCurrentAssistantMessage(thread, event.messageId, now);
      msg.content.push({
        type: 'tool-call',
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        argsText: '',
        args: null,
        status: 'pending',
      } as Draft<ContentPart>);
      break;
    }
    case 'tool-call-args': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        part.argsText += event.delta;
      }
      break;
    }
    case 'tool-call-end': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        setToolCallArgs(part, parseToolArgs(part.argsText));
        part.status = 'executing';
      }
      break;
    }
    case 'tool-call-result': {
      const part = findToolCallPartInThread(thread, event.toolCallId);
      if (part) {
        part.result = event.content;
        part.isError = event.isError;
        part.status = event.isError ? 'error' : 'done';
      }
      break;
    }
  }
}

/**
 * settleTurnOutcome records the current turn's terminal outcome keyed by turnId
 * (from `latestTurnId`) so a settled turn can render its outcome consistently
 * live and after reload. No-op until the turn id is known.
 */
function settleTurnOutcome(thread: Draft<Thread>, outcome: RunOutcome): void {
  if (thread.latestTurnId) {
    thread.turnOutcomes[thread.latestTurnId] = outcome;
  }
}

/**
 * ensureCancelledResponseNode guarantees the cancelled turn has an assistant
 * message node, so a cancel that landed before any streamed content still
 * renders its stopped-response marker. No-op once the node exists or the turn
 * id is unknown.
 */
function ensureCancelledResponseNode(thread: Draft<Thread>, now: number): void {
  if (!thread.latestTurnId) {
    return;
  }
  ensureCurrentAssistantMessage(
    thread,
    assistantMessageId(thread.latestTurnId),
    now
  );
}

/**
 * ensureCurrentAssistantMessage sets the thread's current message to an assistant node
 * with `messageId`, creating and appending a new node if it differs from the
 * current message.
 */
function ensureCurrentAssistantMessage(
  thread: Draft<Thread>,
  messageId: string,
  now: number
): Draft<MessageNode> {
  if (thread.currentMessageId !== messageId) {
    const node: MessageNode = {
      id: messageId,
      role: 'assistant',
      content: [],
      createdAt: now,
    };

    addMessageNode(thread.messageTree, thread.messageTree.currentNodeId, node);
    thread.currentMessageId = messageId;
  }

  return thread.messageTree.nodes[messageId];
}

function settleRun(
  thread: Draft<Thread>,
  runId: string,
  outcome: RunOutcome,
  now: number,
  errorMessage?: string
): void {
  thread.runOutcomes[runId] = outcome;

  // Stale terminal events should stop future stale deltas, but they should not
  // change the visible thread. Only the active run settles parts and clears
  // status — a branch-wide finalize from a stale run would mark the newer
  // run's in-flight parts as interrupted.
  if (runId !== thread.currentRunId) {
    return;
  }

  finalizeInProgressParts(thread, outcome);
  settleTurnOutcome(thread, outcome);
  if (outcome === 'cancelled') {
    ensureCancelledResponseNode(thread, now);
  }

  if (outcome === 'errored') {
    thread.status = 'error';
    thread.error = errorMessage ?? null;
  } else {
    thread.status = 'idle';
  }
  thread.currentRunId = null;
  thread.currentMessageId = null;
  thread.cancelRequested = false;
}

/**
 * finalizeInProgressParts finalizes any content parts still marked as in-
 * progress on the active branch. Call when a run ends (finished or error) to
 * prevent parts from being stuck in a loading state. Sweeping the whole branch
 * rather than only `currentMessageId` matters because a run can touch parts on
 * earlier nodes (e.g. replayed rows for a prior assistant message), and any
 * part left unsettled renders as cancelled in assistant-ui.
 *
 * NOTE: Text and thinking parts transition to 'complete' because their partial
 * content is still valid and displayable. On successful completion, unfinished
 * tool calls get an empty result placeholder so assistant-ui renders them as
 * complete while late result rows can still fill in the real output.
 */
function finalizeInProgressParts(
  thread: Draft<Thread>,
  outcome: RunOutcome
): void {
  for (const msg of activeBranchMessages(thread)) {
    for (const part of msg.content) {
      if (part.type === 'text' || part.type === 'thinking') {
        if (part.status === 'in-progress') {
          part.status = 'complete';
        }
      } else if (part.type === 'tool-call') {
        if (part.status === 'pending' || part.status === 'executing') {
          if (outcome === 'finished') {
            part.status = 'done';
            part.result = part.result ?? '';
            part.isError = false;
          } else {
            part.status = 'error';
            part.result = part.result ?? 'Interrupted';
            part.isError = true;
          }
        }
      }
    }
  }
}

function activeBranchMessages(
  thread: Draft<Thread>
): Array<Draft<MessageNode>> {
  const branch: Array<Draft<MessageNode>> = [];
  let currentNodeId = thread.messageTree.currentNodeId;
  while (currentNodeId) {
    const node = thread.messageTree.nodes[currentNodeId];
    if (!node) {
      break;
    }
    branch.push(node);
    currentNodeId = thread.messageTree.parentId[currentNodeId] ?? null;
  }
  branch.reverse();
  return branch;
}

function findLastPartOfType<T extends ContentPart['type']>(
  content: Draft<ContentPart>[],
  type: T
): Draft<Extract<ContentPart, {type: T}>> | undefined {
  for (let i = content.length - 1; i >= 0; i--) {
    if (content[i].type === type) {
      return content[i] as Draft<Extract<ContentPart, {type: T}>>;
    }
  }

  return undefined;
}

function findToolCallPartInThread(
  thread: Draft<Thread>,
  toolCallId: string
): Draft<ToolCallPart> | undefined {
  const location = findToolCallPart(
    thread.messageTree,
    toolCallId,
    thread.currentMessageId
  );
  if (!location) {
    return undefined;
  }

  return thread.messageTree.nodes[location.nodeId].content[
    location.partIndex
  ] as Draft<ToolCallPart>;
}

function parseToolArgs(
  argsText: string
): Record<string, ReadonlyJSONValue> | null {
  try {
    return JSON.parse(argsText);
  } catch {
    return null;
  }
}

function replaceAssistantContent(
  thread: Draft<Thread>,
  messageId: string,
  content: AssistantContentPart[],
  now: number
): void {
  const existing = thread.messageTree.nodes[messageId];
  if (!existing && content.length === 0) {
    return;
  }

  const msg = existing ?? ensureCurrentAssistantMessage(thread, messageId, now);
  // Streaming owns latency, not truth. Replace the whole assistant snapshot so
  // canonical interleaving and grouping boundaries match a fresh hydration.
  msg.content = castDraft(content);
}

function setToolCallArgs(
  part: Draft<ToolCallPart>,
  args: ToolCallPart['args']
): void {
  (part as unknown as ToolCallPart).args = args;
}

function handleStreamingStart(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string,
  now: number
): void {
  const msg = ensureCurrentAssistantMessage(thread, messageId, now);
  const existing = findLastPartOfType(msg.content, partType);
  if (existing?.status === 'in-progress') {
    return;
  }
  msg.content.push({
    type: partType,
    text: '',
    status: 'in-progress',
  } as Draft<ContentPart>);
}

function handleStreamingDelta(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string,
  delta: string
): void {
  const msg = thread.messageTree.nodes[messageId];
  if (!msg) {
    return;
  }

  const part = findLastPartOfType(msg.content, partType);
  if (part) {
    part.text += delta;
  }
}

function handleStreamingEnd(
  thread: Draft<Thread>,
  partType: 'text' | 'thinking',
  messageId: string
): void {
  const msg = thread.messageTree.nodes[messageId];
  if (!msg) {
    return;
  }

  const part = findLastPartOfType(msg.content, partType);
  if (part) {
    part.status = 'complete';
  }
}
